import classNames from "classnames";
import { CalendarIcon, ClockIcon } from "@icons";
import { ButtonWithLoader } from "@design-system/Button/ButtonWithLoader";
import { HStack } from "@design-system/HStack/HStack";
import { VStack } from "@design-system/VStack/VStack";
import type { TemplateWizardVariant } from "./ApplyCloseTemplateWizardBody";
import styles from "./ApplyCloseTemplateDrawer.module.scss";
import chooseTypeStyles from "./ApplyCloseTemplateStepChooseType.module.scss";

interface Props {
    variant: TemplateWizardVariant | null;
    onSelectVariant: (variant: TemplateWizardVariant) => void;
    onContinue: () => void;
    /** Total steps in the wizard for the selected (or default) variant. */
    totalSteps: number;
}

const TYPE_OPTIONS: Array<{
    value: TemplateWizardVariant;
    title: string;
    description: string;
    Icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
}> = [
    {
        value: "apply",
        title: "One-off",
        description: "Apply this template once to the clients you choose now.",
        Icon: CalendarIcon,
    },
    {
        value: "schedule",
        title: "Recurring",
        description:
            "Set up a recurring schedule that applies this template automatically going forward.",
        Icon: ClockIcon,
    },
];

export const ApplyCloseTemplateStepChooseType = ({
    variant,
    onSelectVariant,
    onContinue,
    totalSteps,
}: Props) => (
    <div>
        <div className={styles.subtitleContainer}>
            Step 1 of {totalSteps} · Choose type
        </div>
        <div className={styles.centeredContent}>
            <div className={styles.serviceDetailsHeader}>
                How do you want to apply this template?
            </div>
            <VStack gap={16} className={chooseTypeStyles.optionsContainer}>
                {TYPE_OPTIONS.map((option) => (
                    <button
                        key={option.value}
                        type="button"
                        className={classNames(chooseTypeStyles.option, {
                            [chooseTypeStyles.optionSelected]:
                                variant === option.value,
                        })}
                        onClick={() => onSelectVariant(option.value)}
                    >
                        <div className={chooseTypeStyles.optionIcon}>
                            <option.Icon />
                        </div>
                        <VStack gap={4}>
                            <div className={chooseTypeStyles.optionTitle}>
                                {option.title}
                            </div>
                            <div className={chooseTypeStyles.optionDescription}>
                                {option.description}
                            </div>
                        </VStack>
                    </button>
                ))}
            </VStack>

            <HStack as="footer" justify="end" gap={12} className="mt-4">
                <ButtonWithLoader
                    variant="primary"
                    size="xl"
                    type="button"
                    loading={false}
                    disabled={variant === null}
                    onClick={onContinue}
                    hotKey="mod+enter"
                    hotKeyOptions={{
                        description: "Continue",
                        preventDefault: true,
                    }}
                    hotKeyDisplayPosition="inline"
                >
                    Continue
                </ButtonWithLoader>
            </HStack>
        </div>
    </div>
);
